import { useContext } from 'react'

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    IconButton,
    makeStyles,
    Typography
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import CloseIcon from '@material-ui/icons/Close'

import { SpacedGroup, StepsContext, StepsStatus, VideoStatusContext } from 'UI/Components'
import { ProgressList } from './ProgressList'
import { Convert } from './Convert'

import { ReactComponent as MagicIcon } from 'assets/icons/auto_fix.svg'

import { useAuth, useBoolean, useRoles } from 'hooks'
import { PlaybookType } from 'app/types'

const useStyles = makeStyles(theme => ({
    accordion: {
        position: 'fixed',
        bottom: '5rem',
        right: '1rem',
        zIndex: 4
    },
    headerRoot: {
        backgroundColor: 'black',
        color: '#fff',
        fontWeight: theme.fontWeightBold
    },
    header: {
        width: '15rem',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    headerExpanded: {
        minHeight: '56px !important',
        margin: '0 !important'
    },
    resetPadding: {
        padding: 0
    }
}))

type Props = {
    playbook: PlaybookType
}

export const StepsStatusBox = ({ playbook }: Props) => {
    const classes = useStyles()

    const { uploadedByOrgId, creator_uid, steps } = playbook

    const closed = useBoolean()

    const { status, stepsProgress } = useContext(StepsContext)
    const { checkVideoStatus } = useContext(VideoStatusContext)

    const { uid } = useAuth()
    const isOwner = uid === creator_uid
    const { isContentManager, isAdmin, isSuperAdmin } = useRoles(uploadedByOrgId)

    const canEdit = isOwner || isContentManager || isAdmin || isSuperAdmin

    // Do now render before animation starts or popup was closed
    if (
        [StepsStatus.idle, StepsStatus.generating, StepsStatus.skipped].includes(status) ||
        closed.isTrue
    ) {
        return null
    }

    // Hide if processing is Done but user doesn't have permission to convert
    if (!canEdit && status === StepsStatus.done) return null

    return (
        <Accordion defaultExpanded className={classes.accordion}>
            <AccordionSummary
                classes={{
                    root: classes.headerRoot,
                    content: classes.header,
                    expanded: classes.headerExpanded
                }}
            >
                <SpacedGroup spacing={1}>
                    <MagicIcon width={24} height={24} fontSize="2rem" style={{ fontSize: 30 }} />
                    <Typography>Magic capture {status === StepsStatus.typing ? 1 : 2}/2</Typography>
                </SpacedGroup>

                <Box display="flex">
                    <IconButton color="inherit" size="small">
                        <ExpandMoreIcon style={{ color: '#75767C' }} />
                    </IconButton>
                    <IconButton
                        color="inherit"
                        size="small"
                        onClick={event => {
                            event.stopPropagation()
                            closed.setTrue()
                        }}
                    >
                        <CloseIcon fontSize="small" style={{ color: '#75767C' }} />
                    </IconButton>
                </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.resetPadding}>
                {[
                    /* @TODO think about adding StepsStatus.generating */
                    StepsStatus.typing
                ].includes(status) && (
                    <ProgressList
                        hasCover={steps?.[0].kind === 'cover'}
                        hasEnd={steps?.[steps.length - 1].kind === 'end'}
                        stepsProgress={stepsProgress}
                    />
                )}

                {status === StepsStatus.done && (
                    <Convert
                        playbook={playbook}
                        onDone={checkVideoStatus}
                        onClose={closed.setTrue}
                    />
                )}
            </AccordionDetails>
        </Accordion>
    )
}
